import React from "react";
import { X, Lock, CreditCard } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

interface PaywallModalProps {
  posit: {
    id: string;
    title: string;
    subscription_price?: number | null;
    creator_name?: string;
    creator_handle?: string;
    creator_first_name?: string;
  };
  onPurchase: () => void;
  onCancel: () => void;
}

const PaywallModal: React.FC<PaywallModalProps> = ({ posit, onPurchase, onCancel }) => {
  // Use the actual subscription price from the posit, with a fallback to a reasonable default
  const subscriptionPrice = posit.subscription_price || 5.00;
  const creatorName = posit.creator_name || posit.creator_first_name || posit.creator_handle || 'Creator';

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: price % 1 === 0 ? 0 : 2
    }).format(price);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <DialogTitle className="text-center">Premium Content</DialogTitle>
          <DialogDescription className="text-center">
            This Posit requires a subscription to access
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Posit Info Card */}
          <Card>
            <CardContent className="p-4">
              <div className="text-center space-y-2">
                <h3 className="font-semibold text-lg leading-tight">{posit.title}</h3>
                <p className="text-sm text-muted-foreground">
                  by {creatorName}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <div className="text-center">
            <div className="text-3xl font-bold mb-2">
              {formatPrice(subscriptionPrice)}
              <span className="text-lg font-normal text-muted-foreground">/month</span>
            </div>
            <Badge variant="secondary" className="mb-4">
              Monthly Subscription
            </Badge>
          </div>

          {/* Benefits */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span>Unlimited questions and conversations</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span>Full access to all content</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span>Cancel anytime</span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button 
              onClick={onPurchase}
              className="w-full"
              size="lg"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Subscribe for {formatPrice(subscriptionPrice)}/month
            </Button>
            
            <Button 
              variant="outline"
              onClick={onCancel}
              className="w-full"
            >
              Browse Other Posits
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal; 